import React from "react";
import { StatusBadge } from "@/components/StatusBadge";
import { money, fmtDate, fmtDateTime } from "@/lib/format";
import { Loader } from "@/components/Loader";
import { EmptyState } from "@/components/EmptyState";
import { ChevronRight } from "lucide-react";

function renderCell(col, row, currency) {
  const raw = row[col.key];
  if (col.render) return col.render(row);
  if (col.type === "badge") return <StatusBadge value={raw} />;
  if (col.type === "money") return <span className="font-mono text-slate-800">{money(raw, currency)}</span>;
  if (col.type === "date") return fmtDate(raw);
  if (col.type === "datetime") return fmtDateTime(raw);
  if (col.type === "number") return <span className="font-mono">{raw ?? 0}</span>;
  if (raw === undefined || raw === null || raw === "") return <span className="text-slate-400">—</span>;
  return String(raw);
}

export function DataTable({ columns, rows, loading, currency = "NGN", onRowClick, emptyTitle, emptyDescription, emptyAction, onEmptyAction, testId }) {
  if (loading) return <Loader />;
  if (!rows || rows.length === 0)
    return <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} onAction={onEmptyAction} testId="table-empty" />;

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              {columns.map((c) => (
                <th key={c.key} className="whitespace-nowrap px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {c.label}
                </th>
              ))}
              {onRowClick && <th className="w-10 px-2" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, i) => (
              <tr
                key={row.id || row._id || i}
                data-testid={`table-row-${i}`}
                onClick={() => onRowClick?.(row)}
                className={`transition-colors ${onRowClick ? "cursor-pointer hover:bg-slate-50" : ""}`}
              >
                {columns.map((c) => (
                  <td key={c.key} className="whitespace-nowrap px-4 py-3 text-sm text-slate-700">
                    {renderCell(c, row, currency)}
                  </td>
                ))}
                {onRowClick && (
                  <td className="px-2 text-slate-300">
                    <ChevronRight className="h-4 w-4" />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
